import { FC } from 'react';
import classNames from 'classnames';
import styles from './summarystats.module.sass';

// ──────────────────────────────────────────────────────────────
// SUMMARY STATS — contextual delta panel (Pattern C)
// ──────────────────────────────────────────────────────────────

export type DeltaDirection = 'up' | 'down' | 'flat';
export type DeltaSentiment = 'positive' | 'negative' | 'neutral';

export interface StatItem {
  /** Label for the stat (e.g. "MoM", "vs Threshold") */
  label: string;
  /** The delta value to display (e.g. "+1.6%", "-$1.2M") */
  value: string;
  /** Direction of the change */
  direction?: DeltaDirection;
  /** Whether this direction is good or bad. Defaults to up=positive. For metrics like DQ, up=negative. */
  sentiment?: DeltaSentiment;
}

export interface SummaryStatsProps {
  /** Array of stat items */
  items: StatItem[];
  /** Layout direction */
  layout?: 'vertical' | 'horizontal';
  /** Additional CSS class */
  className?: string;
}

/**
 * SummaryStats — panel of 2-4 contextual deltas.
 * Shows MoM, QoQ, YoY or vs Threshold/Prior/Benchmark.
 * Green = positive, red = negative, grey = neutral.
 * Follows Design Bible Pattern C.
 *
 * @example
 * <SummaryStats items={[
 *   { label: 'MoM', value: '+1.6%', direction: 'up', sentiment: 'positive' },
 *   { label: 'QoQ', value: '-0.4pp', direction: 'down', sentiment: 'negative' },
 *   { label: 'YoY', value: '+3.2%', direction: 'up', sentiment: 'positive' },
 * ]} />
 */
const SummaryStats: FC<SummaryStatsProps> = ({
  items,
  layout = 'vertical',
  className,
}) => {
  return (
    <div
      className={classNames(
        styles.summaryStats,
        styles[layout],
        className
      )}
    >
      {items.map((item, i) => {
        const resolvedSentiment = item.sentiment ?? resolveSentiment(item.direction);

        return (
          <div key={i} className={styles.statItem}>
            <span className={styles.statLabel}>{item.label}</span>
            <span
              className={classNames(styles.statValue, styles[resolvedSentiment])}
            >
              {item.direction === 'up' && '↑ '}
              {item.direction === 'down' && '↓ '}
              {item.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

function resolveSentiment(direction?: DeltaDirection): DeltaSentiment {
  if (!direction || direction === 'flat') return 'neutral';
  return direction === 'up' ? 'positive' : 'negative';
}

SummaryStats.displayName = 'SummaryStats';
export default SummaryStats;
